import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Users, Building, User, AlertTriangle, Home, Banknote, Mail, Phone } from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialPartyForm = {
  name: '',
  party_type: 'bv',
  kvk_number: '',
  contact_email: '',
  contact_phone: '',
  notes: ''
};

export const PartiesPage = () => {
  const { canEdit, canDelete } = useAuth();
  const { t } = useLanguage();
  const [parties, setParties] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedParty, setSelectedParty] = useState(null);
  const [form, setForm] = useState(initialPartyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [partiesRes, propertiesRes, loansRes] = await Promise.all([
        axios.get(`${API}/parties`, { withCredentials: true }),
        axios.get(`${API}/realestate`, { withCredentials: true }),
        axios.get(`${API}/loans`, { withCredentials: true })
      ]);
      setParties(partiesRes.data);
      setProperties(propertiesRes.data);
      setLoans(loansRes.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (party = null) => {
    if (party) {
      setSelectedParty(party);
      setForm({
        name: party.name,
        party_type: party.party_type,
        kvk_number: party.kvk_number || '',
        contact_email: party.contact_email || '',
        contact_phone: party.contact_phone || '',
        notes: party.notes || ''
      });
    } else {
      setSelectedParty(null);
      setForm(initialPartyForm);
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (form.party_type === 'bv' && !form.kvk_number) {
      toast.error('KvK nummer is verplicht voor B.V.');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...form,
        kvk_number: form.party_type === 'bv' ? form.kvk_number : null
      };

      if (selectedParty) {
        await axios.put(`${API}/parties/${selectedParty.party_id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/parties`, payload, { withCredentials: true });
      }
      toast.success(t('savedSuccessfully'));
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/parties/${selectedParty.party_id}`, { withCredentials: true });
      toast.success(t('deletedSuccessfully'));
      setDeleteDialogOpen(false);
      setSelectedParty(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const getPropertyCountForParty = (partyId) => {
    return properties.filter(p => 
      p.owners?.some(o => o.party_id === partyId) ||
      p.legal_owners?.some(lo => lo.party_id === partyId || lo.owner_id === partyId)
    ).length;
  };

  const getLoanCountForParty = (partyId) => {
    return loans.filter(l => 
      l.borrower_ids?.includes(partyId) ||
      l.hms_ids?.includes(partyId) ||
      l.borg_ids?.includes(partyId)
    ).length;
  };

  const getPartyRoles = (partyId) => {
    const roles = [];
    
    // Check if party is an owner
    const isOwner = properties.some(p => 
      p.owners?.some(o => o.party_id === partyId) ||
      p.legal_owners?.some(lo => lo.party_id === partyId || lo.owner_id === partyId)
    );
    if (isOwner) roles.push('Eigenaar');
    
    // Check if party is a borrower
    const isBorrower = loans.some(l => l.borrower_ids?.includes(partyId));
    if (isBorrower) roles.push('Kredietnemer');
    
    // Check if party is HMS
    const isHMS = loans.some(l => l.hms_ids?.includes(partyId));
    if (isHMS) roles.push('HMS');
    
    // Check if party is Borg
    const isBorg = loans.some(l => l.borg_ids?.includes(partyId));
    if (isBorg) roles.push('Borg');
    
    return roles;
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64 bg-slate-800" />
          <Skeleton className="h-96 bg-slate-800" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="parties-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white font-['Comfortaa']">{t('parties')}</h1>
            <p className="text-slate-400 mt-1">
              {parties.length} {parties.length === 1 ? 'partij' : 'partijen'} geregistreerd
            </p>
          </div>
          <Button 
            className="bg-[#b4976f] hover:bg-[#a38663]"
            onClick={() => handleOpenDialog()}
            data-testid="add-party-btn"
          >
            <Plus className="mr-2 h-4 w-4" />
            {t('addParty')}
          </Button>
        </div>

        {/* Parties Table */}
        <Card className="bg-[#1e293b] border-slate-700">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700 hover:bg-transparent">
                  <TableHead className="text-slate-400">{t('partyName')}</TableHead>
                  <TableHead className="text-slate-400">{t('partyType')}</TableHead>
                  <TableHead className="text-slate-400">{t('kvkNumber')}</TableHead>
                  <TableHead className="text-slate-400">{t('roles')}</TableHead>
                  <TableHead className="text-slate-400 text-center">
                    <Home className="h-4 w-4 inline mr-1" />
                    {t('realEstate')}
                  </TableHead>
                  <TableHead className="text-slate-400 text-center">
                    <Banknote className="h-4 w-4 inline mr-1" />
                    {t('loans')}
                  </TableHead>
                  <TableHead className="text-slate-400 text-right">{t('edit')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {parties.length > 0 ? (
                  parties.map((party) => {
                    const roles = getPartyRoles(party.party_id);
                    return (
                      <TableRow key={party.party_id} className="border-slate-700 table-row-hover">
                        <TableCell className="text-white font-medium">
                          <div className="flex items-center gap-2">
                            {party.party_type === 'bv' ? (
                              <Building className="h-4 w-4 text-[#b4976f]" />
                            ) : (
                              <User className="h-4 w-4 text-[#c8b599]" />
                            )}
                            <div>
                              <div>{party.name}</div>
                              {(party.contact_email || party.contact_phone) && (
                                <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                                  {party.contact_email && (
                                    <span className="flex items-center gap-1">
                                      <Mail className="h-3 w-3" />
                                      {party.contact_email}
                                    </span>
                                  )}
                                  {party.contact_phone && (
                                    <span className="flex items-center gap-1">
                                      <Phone className="h-3 w-3" />
                                      {party.contact_phone}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            party.party_type === 'bv' 
                              ? 'bg-[#b4976f]/20 text-[#b4976f]' 
                              : 'bg-[#264162]/50 text-[#c8b599]'
                          }`}>
                            {party.party_type === 'bv' ? t('bvOwner') : t('privateOwner')}
                          </span>
                        </TableCell>
                        <TableCell className="text-slate-300">
                          {party.kvk_number || '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {roles.length > 0 ? roles.map((role, idx) => (
                              <span key={idx} className="px-2 py-0.5 rounded text-xs bg-slate-700 text-slate-300">
                                {role}
                              </span>
                            )) : (
                              <span className="text-slate-500 text-xs">Geen rol</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center text-white">
                          {getPropertyCountForParty(party.party_id)}
                        </TableCell>
                        <TableCell className="text-center text-white">
                          {getLoanCountForParty(party.party_id)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenDialog(party)}
                              data-testid={`edit-party-${party.party_id}`}
                            >
                              <Edit className="h-4 w-4 text-slate-400 hover:text-white" />
                            </Button>
                            {canDelete() && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedParty(party);
                                  setDeleteDialogOpen(true);
                                }}
                                data-testid={`delete-party-${party.party_id}`}
                              >
                                <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-400" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-slate-500 py-12">
                      <Users className="h-12 w-12 mx-auto mb-4 text-slate-600" />
                      <p>{t('noData')}</p>
                      <Button 
                        variant="link" 
                        className="text-[#b4976f] mt-2"
                        onClick={() => handleOpenDialog()}
                      >
                        {t('addParty')}
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-[#1e293b] border-slate-700 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-['Comfortaa']">
                {selectedParty ? t('editParty') : t('addParty')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300">{t('partyName')}</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-slate-900 border-slate-700 text-white"
                  required
                  data-testid="party-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-300">{t('partyType')}</Label>
                <Select 
                  value={form.party_type} 
                  onValueChange={(value) => setForm({ ...form, party_type: value })}
                >
                  <SelectTrigger className="bg-slate-900 border-slate-700 text-white" data-testid="party-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="bv" className="text-white hover:bg-slate-800">
                      {t('bvOwner')} (B.V.)
                    </SelectItem>
                    <SelectItem value="private" className="text-white hover:bg-slate-800">
                      {t('privateOwner')}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {form.party_type === 'bv' && (
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('kvkNumber')} *</Label>
                  <Input
                    value={form.kvk_number}
                    onChange={(e) => setForm({ ...form, kvk_number: e.target.value })}
                    className="bg-slate-900 border-slate-700 text-white"
                    required
                    data-testid="kvk-number-input"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-slate-300">{t('contactEmail')}</Label>
                <Input
                  type="email"
                  value={form.contact_email}
                  onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                  className="bg-slate-900 border-slate-700 text-white"
                  data-testid="contact-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">{t('contactPhone')}</Label>
                <Input
                  type="tel"
                  value={form.contact_phone}
                  onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                  className="bg-slate-900 border-slate-700 text-white"
                  data-testid="contact-phone-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">{t('notes')}</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="bg-slate-900 border-slate-700 text-white"
                  rows={3}
                  data-testid="party-notes-input"
                />
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="border-slate-600 text-slate-300"
                >
                  {t('cancel')}
                </Button>
                <Button
                  type="submit"
                  className="bg-[#b4976f] hover:bg-[#a38663]"
                  disabled={saving}
                  data-testid="save-party-btn"
                >
                  {saving ? t('loading') : t('save')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-[#1e293b] border-slate-700">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-400" />
                {t('confirmDelete')}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-slate-400">
                Dit verwijdert de partij "{selectedParty?.name}" permanent.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-slate-600 text-slate-300 hover:bg-slate-800">
                {t('cancel')}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-btn"
              >
                {t('delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};
